import { useEffect, useState } from 'react'
import { onSignedOut } from './sessionSignal'
import { PixelPet } from '../identity/PixelPet'
import { fetchSession, SIGN_IN_APPLE_URL, SIGN_IN_URL, type Session } from './googleSession'
import { nativeAppleAvailable, signInWithAppleNative } from './appleNative'
import { PairApprove, readCodeFromUrl } from '../pair/PairApprove'
import { peekInviteLink } from '../friends/inviteLink'
import { inviteFrom } from '../transport/relay'

/**
 * The way in. Google, and nothing else to fill in.
 *
 * It replaces the vault's username-and-password box, and the difference worth keeping
 * is not that it looks nicer: the vault asked for a secret this app then had to hold,
 * while this hands the whole exchange to Google and comes back with a cookie
 * JavaScript cannot read. There is no field here because there is nothing for the
 * user to type that we would be allowed to keep.
 *
 * THE THREE STATES ARE THE DESIGN. `googleSession` refuses to collapse "signed out"
 * into "cannot reach the thing that would know", and this is where that pays: in
 * development the common failure is that the BFF on 8092 is not running, and a
 * sign-in button offered then is a button that cannot work. So an unreachable BFF
 * gets its own panel that says what to go and do, rather than a button that will
 * silently fail and leave someone doubting their Google account.
 */
export function SignInGate({
  onSignedIn,
  alreadyChecked = false,
}: {
  onSignedIn?: (email: string) => void
  /**
   * The caller has just asked and got "signed out" — do not ask again on mount.
   *
   * `Authed` renders this only after its own check, so the mount check here was the
   * same question a second time. One page load produced four `/auth/me` calls, every
   * one logged by the browser as an error, all describing a state already known.
   *
   * The visibility re-check stays: coming back from the OAuth round trip is exactly
   * when the answer changes, and nobody else is watching for that.
   */
  alreadyChecked?: boolean
}) {
  const [session, setSession] = useState<Session | null>(null)
  /*
   * `alreadyChecked` means the caller has the answer: start as NOT checking.
   *
   * Left at `true` with no check of its own, this renders its loading state forever —
   * a spinner where the sign-in button should be. Every test passed, because none of
   * them passed the new prop.
   */
  /*
   * With `alreadyChecked` the caller already has the answer, so this starts NOT
   * checking. Left at `true` and with its own mount check skipped, nothing ever
   * cleared it and the gate rendered its loading state forever — a spinner where the
   * sign-in button belongs. Every test passed, because none passed the new prop.
   */
  const [checking, setChecking] = useState(!alreadyChecked)
  const [appleBusy, setAppleBusy] = useState(false)
  const [appleNote, setAppleNote] = useState<string | null>(null)
  /*
   * Opened from someone's invite link (friends/inviteLink.ts; owner, 2026-10-19): say who, so the screen a newcomer
   * lands on is an invitation rather than a stranger's door. A link that names nobody says nothing.
   */
  const [invitedBy, setInvitedBy] = useState<string | null>(null)
  useEffect(() => {
    const code = peekInviteLink()
    if (!code) return
    let live = true
    inviteFrom(code).then(
      (r) => live && setInvitedBy(r.name || null),
      () => {},
    )
    return () => {
      live = false
    }
  }, [])

  /*
   * The native sheet, where there is one (an iOS shell with the plugin compiled
   * in). It hands the app a token, the BFF mints the session, and the page
   * RELOADS into it — the same no-residue rule as Sign out, and the same
   * landing the web flow's redirect produces.
   */
  const appleNative = async () => {
    setAppleBusy(true)
    setAppleNote(null)
    try {
      const r = await signInWithAppleNative()
      if (r === 'ok') {
        window.location.replace('/')
        return
      }
      if (r === 'failed') setAppleNote('Apple sign-in did not go through. Try again.')
    } finally {
      setAppleBusy(false)
    }
  }

  const check = async () => {
    setChecking(true)
    const s = await fetchSession()
    setSession(s)
    setChecking(false)
    if (s.kind === 'in') onSignedIn?.(s.identity.email)
  }

  /*
   * Re-check when a request comes back refused.
   *
   * This gate used to ask once, at mount. A session that ended while the page stayed
   * open therefore never surfaced: the interface remained, and every button quietly
   * did nothing — which is exactly how a login expiring after thirty minutes was read
   * as a delivery fault by three services for an hour. The signal only fires because a
   * request the user themself made was refused, so nothing here asks on a timer.
   */
  useEffect(() => onSignedOut(() => void check()), [])

  useEffect(() => {
    if (!alreadyChecked) void check()
    // Re-check when the tab regains focus: the OAuth round trip leaves and comes back,
    // and on the way back this component may never unmount — so a stale "signed out"
    // would survive the sign-in that just happened.
    const onVisible = () => {
      if (!document.hidden) void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Signed in: this gate has nothing to say. The app renders behind it.
  if (session?.kind === 'in') return null

  const state = checking && session === null ? 'checking' : session?.kind === 'unreachable' ? 'unreachable' : 'out'

  return (
    <div
      className={`sg-wrap sg-${state}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sgTitle"
    >
      {/* A pairing link's landing must not hide behind the login it usually
          precedes — the approve leg runs its own Google dance, so the banner's
          action is valid signed out. The code ALSO rides signInUrl(), so the
          slower road (sign in first) arrives with the banner still up. */}
      <PairApprove />
      <div className="sg-box">
        <div className="sg-topline" aria-hidden="true">
          <span className="sg-node on" />
          <span className="sg-node mid" />
          <span className="sg-node off" />
        </div>

        <div className="sg-brand">
          {/* The app's own mascot rather than a logo: it is what every agent row already
              wears, so the first screen introduces the vocabulary the rest uses. */}
          <div className="sg-pet" aria-hidden="true">
            <PixelPet name="avengents" size={64} />
          </div>

          <div className="sg-brand-text">
            <p className="sg-kicker">Humans + agents</p>
            <h1 id="sgTitle" className="sg-title">
              Avengents
            </h1>
          </div>
        </div>

        {checking && session === null ? (
          // First load only. A spinner on every re-check would flash on tab focus.
          <div className="sg-state">
            <span className="sg-status">Checking session</span>
            <p className="sg-copy">Checking your session…</p>
          </div>
        ) : session?.kind === 'unreachable' ? (
          <>
            <div className="sg-state">
              <span className="sg-status">Connection issue</span>
              <p className="sg-copy sg-warn">Sign-in is not answering.</p>
              <p className="sg-state-copy">
                We cannot tell whether you are signed in until the sign-in service responds.
              </p>
            </div>
            <div className="sg-detail-box">
              <span className="sg-detail-label">Diagnostic</span>
              <p className="sg-detail">
                {/* The detail is shown, not swallowed. In dev this is almost always
                    "8092 is not running", and that is a terminal away — but only if the
                    message says so instead of blaming the account. */}
                {session.detail}
              </p>
            </div>
            <button type="button" className="sg-secondary" onClick={() => void check()}>
              Try again
            </button>
          </>
        ) : (
          <>
            <div className="sg-state">
              <span className="sg-status">Ready to connect</span>
              <p className="sg-copy">
                {invitedBy ? `${invitedBy} invited you to Avengents — sign in to join.` : 'Sign in to continue.'}
              </p>
            </div>
            {/*
             * An anchor, not a button with a fetch. `/auth/google/start` answers 302
             * to Google, and a fetch would follow that redirect in the background and
             * land the user nowhere. The browser has to navigate.
             */}
            <a className="sg-google" href={signInUrl()}>
              <GoogleMark />
              <span>Sign in with Google</span>
            </a>
            {/*
             * The second door — App Store guideline 4.8: an app whose primary
             * account is a third-party login must offer one with Apple's privacy
             * properties. Same shape as Google's: an anchor to a 302, never a fetch.
             */}
            {nativeAppleAvailable() ? (
              // The shell's own sheet (appleNative.ts); a button, because the
              // credential never leaves the device as a navigation.
              <button type="button" className="sg-apple" onClick={() => void appleNative()} disabled={appleBusy}>
                <AppleMark />
                <span>{appleBusy ? 'Signing in…' : 'Sign in with Apple'}</span>
              </button>
            ) : (
              <a className="sg-apple" href={signInUrl(SIGN_IN_APPLE_URL)}>
                <AppleMark />
                <span>Sign in with Apple</span>
              </a>
            )}
            {appleNote && <p className="sg-fine sg-warn">{appleNote}</p>}
            <p className="sg-fine">
              Avengents never sees your password. Google or Apple confirms who you are, and
              this app only learns your email address. Each sign-in method is its own
              account, so use the same one on every device.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Google's mark, inline.
 *
 * Inline rather than loaded from Google: a remote image on the sign-in screen is a
 * request to a third party before anyone has agreed to anything, and it fails exactly
 * when the network is the problem — leaving a broken image on the one screen that has
 * to look trustworthy.
 */
function GoogleMark() {
  return (
    <svg className="sg-gmark" viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58z" />
    </svg>
  )
}

/**
 * Apple's mark, inline, for the same reason as Google's: no request to a third
 * party before anyone has agreed to anything. Apple's own guideline for the
 * button: the logo and the words "Sign in with Apple", black on white or white
 * on black — the latter here, on this screen's dark ground.
 */
function AppleMark() {
  return (
    <svg className="sg-amark" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.37 12.74c-.02-2.08 1.7-3.08 1.78-3.13-.97-1.42-2.48-1.61-3.02-1.64-1.29-.13-2.51.76-3.16.76-.65 0-1.66-.74-2.72-.72-1.4.02-2.69.81-3.41 2.07-1.46 2.53-.37 6.27 1.05 8.32.69 1 1.52 2.13 2.6 2.09 1.04-.04 1.44-.68 2.7-.68 1.26 0 1.62.68 2.72.66 1.12-.02 1.83-1.02 2.52-2.03.79-1.17 1.12-2.3 1.14-2.36-.02-.01-2.19-.84-2.2-3.34zM14.3 6.62c.57-.69.96-1.65.85-2.62-.82.03-1.82.55-2.41 1.24-.53.61-.99 1.59-.87 2.53.92.07 1.86-.47 2.43-1.15z"
      />
    </svg>
  )
}

/**
 * The sign-in URL, carrying a pairing code across the dance when the page
 * arrived with one — owner, 2026-08-13: 9445 的配對 link 無法使用. The person
 * opening `avengents pair`'s printed link is USUALLY signed out (a new machine
 * is why they are here), and the BFF's auth_start exists to carry exactly this
 * (§4.2b: "the phone that scans it is usually not signed in"). Without it the
 * code died at Google and post-login showed a plain app. Only a shape-valid
 * code rides along, re-encoded; garbage keeps the plain URL. The Apple door
 * carries it the same way.
 */
export function signInUrl(base: string = SIGN_IN_URL): string {
  const code = readCodeFromUrl()
  return code ? `${base}?code=${encodeURIComponent(code)}` : base
}
